import uniAdminApi, { translateErrorCode } from "./api";
import * as tokenStorage from "./tokenStorage";

function userFromAccessToken(accessToken) {
  try {
    const parts = accessToken.split(".");
    if (parts.length !== 3) return null;
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json);
    return {
      email: claims.sub,
      role: claims.role,
      universityId: claims.university_id ?? null,
    };
  } catch {
    return null;
  }
}

class UniAdminAuthService {
  async login(email, password, remember = false) {
    try {
      const response = await uniAdminApi.post("/university-admin/login", {
        email,
        password,
      });

      const payload = response.data?.data;
      const accessToken = payload?.accessToken;

      if (response.data?.success && accessToken) {
        tokenStorage.setTokens(accessToken, payload.refreshToken, remember);

        const user = userFromAccessToken(accessToken) || (email ? { email } : null);
        return { success: true, data: { ...payload, user } };
      }

      return {
        success: false,
        error: translateErrorCode(response.data?.errorCode),
      };
    } catch (error) {
      return {
        success: false,
        error: translateErrorCode(error.response?.data?.errorCode),
      };
    }
  }

  async forgottenPassword(email) {
    try {
      const response = await uniAdminApi.post("/users/forgotten-password", { email });
      return { success: !!response.data?.success, error: response.data?.errorCode || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.errorCode || "Request failed" };
    }
  }

  async resetPassword(email, otp, newPassword) {
    try {
      const response = await uniAdminApi.post("/users/reset-password", {
        email,
        otp,
        newPassword,
      });
      return { success: !!response.data?.success, error: response.data?.errorCode || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.errorCode || "Request failed" };
    }
  }

  async changePassword(oldPassword, newPassword) {
    try {
      const response = await uniAdminApi.post("/users/change-password", {
        oldPassword,
        newPassword,
      });
      return { success: !!response.data?.success, error: response.data?.errorCode || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.errorCode || "Request failed" };
    }
  }

  async logout() {
    const refreshToken = tokenStorage.getRefreshToken();
    try {
      if (refreshToken) {
        await uniAdminApi.post("/api/v1/auth/logout", { refreshToken });
      }
    } catch {
      // ignore
    } finally {
      tokenStorage.clearTokens();
      window.location.href = "/uni-admin/login";
    }
  }

  isAuthenticated() {
    return !!tokenStorage.getAccessToken();
  }

  getCurrentUser() {
    const token = tokenStorage.getAccessToken();
    if (!token) return null;
    return userFromAccessToken(token);
  }

  getToken() {
    return tokenStorage.getAccessToken();
  }

  getRefreshToken() {
    return tokenStorage.getRefreshToken();
  }

  async getMyPermissions() {
    const res = await uniAdminApi.get("/university-admin/me/permissions");
    return res.data;
  }
}

const uniAdminAuthService = new UniAdminAuthService();
export default uniAdminAuthService;
